import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { TRequiredRoles } from '../../utils/types/authTypes'
import { PAGE_URLS } from '../../utils/helpers/helper'
import { useAuthContext } from '../../context/auth/AuthContextProvider'

const RequireAuth = ({
  requiredRoles,
  requiredCompanyOption,
}: TRequiredRoles) => {
  const location = useLocation()
  const { user } = useAuthContext()

  const { passwordResetAt } = user
  const isUserLockedToZone = user.companyId && user.companyId > 0
  const isUserLockedToDepot = user.depotId && user.depotId > 0

  if (
    location.pathname !== PAGE_URLS.CHANGE_PASSWORD &&
    passwordResetAt > 0 &&
    passwordResetAt * 1000 < new Date().getTime()
  ) {
    return (
      <Navigate
        to={PAGE_URLS.CHANGE_PASSWORD}
        state={{ from: location }}
        replace
      />
    )
  }

  const isRestrictedMapPage = [
    PAGE_URLS.MAP_VIEW,
    PAGE_URLS.ROUTES_PLAN,
  ].includes(location.pathname)

  if (isRestrictedMapPage && !user.organisationMapsApiKey) {
    return (
      <Navigate
        to={PAGE_URLS.UNAUTHORIZED}
        state={{ from: location }}
        replace
      />
    )
  }

  if (user.organisationId === 0) {
    return <Navigate to="/" state={{ from: location }} replace />
  }

  const hasRequiredRole = user.roles.some((role: string) =>
    requiredRoles.includes(role)
  )

  if (!hasRequiredRole) {
    return (
      <Navigate
        to={PAGE_URLS.UNAUTHORIZED}
        state={{ from: location }}
        replace
      />
    )
  }

  if (requiredCompanyOption) {
    const hasCompanyOption = (user.companyOptions || []).some(
      (option: string) => requiredCompanyOption.includes(option)
    )

    if (!hasCompanyOption) {
      return (
        <Navigate
          to={PAGE_URLS.NO_COMPANY_OPTION}
          state={{ from: location }}
          replace
        />
      )
    }
  }

  // If user locked to depot, redirect to unauthorized page if try to access any depots section
  if (
    isUserLockedToDepot &&
    location.pathname.includes(PAGE_URLS.SETTINGS_DEPOTS)
  ) {
    return (
      <Navigate
        to={PAGE_URLS.UNAUTHORIZED}
        state={{ from: location }}
        replace
      />
    )
  }

  // If user locked to zone, redirect to unauthorized page if try to access any zones section
  if (
    isUserLockedToZone &&
    location.pathname.includes(PAGE_URLS.SETTINGS_ZONES)
  ) {
    return (
      <Navigate
        to={PAGE_URLS.UNAUTHORIZED}
        state={{ from: location }}
        replace
      />
    )
  }

  return <Outlet />
}

export default RequireAuth
